"use client";

import { Input } from "@/components/ui/input";

import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGlobalLoader } from "./GlobalLoader";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  autoResetPageIndex?: boolean;
  onRowClick?: (row: TData) => void;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  autoResetPageIndex = true,
  onRowClick,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter] = useState("");
  const [selectedColumn, setSelectedColumn] = useState<string>(""); // coluna selecionada
  const [columnFilterValue, setColumnFilterValue] = useState(""); // valor do filtro
  const { isLoading } = useGlobalLoader();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    autoResetPageIndex,
    state: {
      columnFilters,
      globalFilter,
    },
    globalFilterFn: "includesString",
  });

  // Lista de colunas filtráveis (com accessorKey)
  const filterableColumns = useMemo(
    () =>
      table
        .getAllColumns()
        .filter(
          (col) => "accessorKey" in col.columnDef && !!col.columnDef.accessorKey
        ),
    [table]
  );

  // Atualiza filtro de coluna
  const handleColumnFilter = (col: string, value: string) => {
    setColumnFilters((prev) => {
      // Remove filtro antigo da coluna, se existir
      const filtered = prev.filter((f) => f.id !== col);
      // Adiciona novo filtro se houver valor
      return value ? [...filtered, { id: col, value }] : filtered;
    });
  };

  return (
    <div>
      <div className="flex items-center gap-2 py-4">
        <Select
          value={selectedColumn}
          onValueChange={(value) => {
            setSelectedColumn(value);
            setColumnFilterValue(""); // Limpa o valor do filtro ao mudar a coluna
            handleColumnFilter(value, ""); // Remove filtro da coluna selecionada
          }}
        >
          <SelectTrigger
            className="flex w-40 **:data-[slot=select-value]:block **:data-[slot=select-value]:truncate @[767px]/card:hidden"
            size="sm"
            aria-label={t("ui.selectColumnAria")}
          >
            <SelectValue placeholder={t("ui.selectColumn")} />
          </SelectTrigger>

          <SelectContent>
            {filterableColumns.map((col) => (
              <SelectItem key={col.id} value={col.id}>
                {typeof col.columnDef.header === "string"
                  ? col.columnDef.header
                  : col.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder={t("ui.filterValue")}
          value={columnFilterValue}
          onChange={(e) => {
            setColumnFilterValue(e.target.value);
            if (selectedColumn) {
              handleColumnFilter(selectedColumn, e.target.value);
            }
          }}
          className="max-w-sm"
          disabled={!selectedColumn}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setColumnFilterValue("");
            setSelectedColumn("");
            setColumnFilters([]); // Limpa todos os filtros
          }}
          disabled={!selectedColumn && !columnFilterValue}
        >
          {t("ui.clearFilters")}
        </Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => onRowClick && onRowClick(row.original)}
                  className={
                    onRowClick ? "cursor-pointer hover:bg-muted/50" : ""
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {isLoading ? t("ui.loading") : t("ui.noData")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {table.getPageCount() > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 rounded-md border p-2 mt-4">
          <p className="text-xs text-muted-foreground">
            {t("ui.page")}{" "}
            <strong className="text-foreground">
              {table.getState().pagination.pageIndex + 1}
            </strong>{" "}
            {t("ui.of")}{" "}
            <strong className="text-foreground">{table.getPageCount()}</strong>
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.setPageIndex(0)}
              aria-label={t("ui.firstPage")}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
              aria-label={t("ui.previousPage")}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {(() => {
              const pageIndex = table.getState().pagination.pageIndex;
              const pageCount = table.getPageCount();
              const items: (number | "ellipsis")[] = [];
              const current = pageIndex + 1;
              if (pageCount <= 7) {
                for (let i = 1; i <= pageCount; i++) items.push(i);
              } else {
                items.push(1);
                if (current > 3) items.push("ellipsis");
                const start = Math.max(2, current - 1);
                const end = Math.min(pageCount - 1, current + 1);
                for (let i = start; i <= end; i++) items.push(i);
                if (current < pageCount - 2) items.push("ellipsis");
                items.push(pageCount);
              }
              return items.map((it, i) =>
                it === "ellipsis" ? (
                  <span
                    key={`e-${i}`}
                    className="text-xs text-muted-foreground px-1"
                  >
                    …
                  </span>
                ) : (
                  <Button
                    key={it}
                    variant={it === current ? "default" : "outline"}
                    size="sm"
                    className="h-8 min-w-[2rem] px-2 text-xs"
                    onClick={() => table.setPageIndex(it - 1)}
                  >
                    {it}
                  </Button>
                ),
              );
            })()}
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
              aria-label={t("ui.nextPage")}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!table.getCanNextPage()}
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              aria-label={t("ui.lastPage")}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
